import { DIRECTION_DIAGONAL_LEFT_UP, DIRECTION_DIAGONAL_RIGHT_UP, DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../../pzl_common/PuzzleUtil";
import { MAX_WORD_LENGTH, MIN_WORD_LENGTH } from "../../../pzl_common/WordCandConstants";

// Normalize word for grid placement: remove hyphens and spaces (e.g., "run on" -> "runon", "ex-wife" -> "exwife")
export const getGridWord = (word) => {
    if (!word || typeof word !== "string") return "";
    return word.replace(/[-\s]/g, "");
};

// Generate positions for word placement based on direction
export const generatePositions = (startRow, startCol, direction, length, word = "", isReversed = false) => {
    const positions = [];

    for (let i = 0; i < length; i++) {
        let row = startRow;
        let col = startCol;

        switch (direction) {
            case DIRECTION_HORIZONTAL:
                if (isReversed) {
                    // For reversed horizontal, go from right to left
                    // Calculate positions as if going backwards from the starting point
                    col = startCol - (length - 1) + i;
                } else {
                    // Normal horizontal, go from left to right
                    col += i;
                }
                break;
            case DIRECTION_VERTICAL:
                if (isReversed) {
                    // For reversed vertical, go from bottom to top
                    // Calculate positions as if going backwards from the starting point
                    row = startRow - (length - 1) + i;
                } else {
                    // Normal vertical, go from top to bottom
                    row += i;
                }
                break;
            case DIRECTION_DIAGONAL_RIGHT_UP:
                if (isReversed) {
                    // For reversed diagonal right-up, go from bottom-left to top-right
                    // Start at the given position and go backwards
                    row = startRow - i;
                    col = startCol + i;
                } else {
                    // Normal diagonal right-up, go from top-right to bottom-left
                    row += i;
                    col -= i;
                }
                break;
            case DIRECTION_DIAGONAL_LEFT_UP:
                if (isReversed) {
                    // For reversed diagonal left-up, go from bottom-right to top-left
                    // Start at the given position and go backwards
                    row = startRow - i;
                    col = startCol - i;
                } else {
                    // Normal diagonal left-up, go from top-left to bottom-right
                    row += i;
                    col += i;
                }
                break;
            default:
                break;
        }

        // Get the character from the word if provided, otherwise leave empty
        // The word characters should match the pattern order exactly
        const value = word && i < word.length ? word[i].toLowerCase() : "";
        positions.push({ row, col, value });
    }

    return positions;
};

// Board size from square (rows); defaults to 12
export const getSquareSize = (square) => (Array.isArray(square) && square.length > 0 ? square.length : 12);

// Generate horizontal pattern (left to right)
export const generateHorizontalPattern = (square, startRow, startCol) => {
    const size = getSquareSize(square);
    let pattern = "";

    // Find the actual start of the word (look for leftmost position)
    let actualStartCol = startCol;
    while (actualStartCol > 0 && (square[startRow][actualStartCol - 1] || square[startRow][actualStartCol - 1] === "")) {
        actualStartCol--;
    }

    // Generate pattern from actual start (left to right)
    for (let col = actualStartCol; col < size; col++) {
        const char = square[startRow][col];
        if (char && char !== "") {
            pattern += char;
        } else {
            pattern += "?";
        }

        // Stop if we hit a boundary or pattern is too long
        if (col === size - 1 || pattern.length >= size) break;

        // Stop if next cell is definitely blocked (undefined means out of bounds)
        if (col + 1 < size && square[startRow][col + 1] === undefined) break;

        // Don't stop early - let the pattern grow as long as possible
        // This will find patterns like "CAT???" or "?AT??" or "??T??"
    }

    return { pattern, actualStartCol };
};

// Generate vertical pattern (top to bottom)
export const generateVerticalPattern = (square, startRow, startCol) => {
    const size = getSquareSize(square);
    let pattern = "";

    // Find the actual start of the word (look for topmost position)
    let actualStartRow = startRow;
    while (actualStartRow > 0 && (square[actualStartRow - 1][startCol] || square[actualStartRow - 1][startCol] === "")) {
        actualStartRow--;
    }

    // Generate pattern from actual start (top to bottom)
    for (let row = actualStartRow; row < size; row++) {
        const char = square[row][startCol];
        if (char && char !== "") {
            pattern += char;
        } else {
            pattern += "?";
        }

        // Stop if we hit a boundary or pattern is too long
        if (row === size - 1 || pattern.length >= size) break;

        // Stop if next cell is definitely blocked
        if (row + 1 < size && square[row + 1][startCol] === undefined) break;

        // Don't stop early - let the pattern grow as long as possible
        // This will find patterns like "CAT???" or "?AT??" or "??T??"
    }

    return { pattern, actualStartRow };
};

// Generate diagonal right-up pattern (top-right to bottom-left)
export const generateDiagonalRightUpPattern = (square, startRow, startCol) => {
    const size = getSquareSize(square);
    let pattern = "";

    // Find the actual start (look for top-rightmost position)
    let actualStartRow = startRow;
    let actualStartCol = startCol;
    while (actualStartRow > 0 && actualStartCol < size - 1 && 
           (square[actualStartRow - 1][actualStartCol + 1] || square[actualStartRow - 1][actualStartCol + 1] === "")) {
        actualStartRow--;
        actualStartCol++;
    }

    // Generate pattern from actual start (top-right to bottom-left)
    for (let i = 0; i < size; i++) {
        const row = actualStartRow + i;
        const col = actualStartCol - i;

        if (row >= size || col < 0) break;

        const char = square[row][col];
        if (char && char !== "") {
            pattern += char;
        } else {
            pattern += "?";
        }
    }

    return pattern;
};

// Generate diagonal left-up pattern (top-left to bottom-right)
export const generateDiagonalLeftUpPattern = (square, startRow, startCol) => {
    const size = getSquareSize(square);
    let pattern = "";

    // Find the actual start (look for top-leftmost position)
    let actualStartRow = startRow;
    let actualStartCol = startCol;
    while (actualStartRow > 0 && actualStartCol > 0 && 
           (square[actualStartRow - 1][actualStartCol - 1] || square[actualStartRow - 1][actualStartCol - 1] === "")) {
        actualStartRow--;
        actualStartCol--;
    }

    // Generate pattern from actual start (top-left to bottom-right)
    for (let i = 0; i < size; i++) {
        const row = actualStartRow + i;
        const col = actualStartCol + i;

        if (row >= size || col >= size) break;

        const char = square[row][col];
        if (char && char !== "") {
            pattern += char;
        } else {
            pattern += "?";
        }
    }

    return pattern;
};

// Find empty spaces in all directions and generate word candidates
export const findEmptySpaces = (square) => {
    const emptySpaces = [];
    const size = getSquareSize(square);
    const processedPatterns = new Set(); // Track processed patterns to avoid duplicates

    // Helper function to calculate consecutiveness score
    const calculateConsecutivenessScore = (pattern) => {
        let maxConsecutive = 0;
        let currentConsecutive = 0;
        let totalEmpty = 0;
        
        for (let i = 0; i < pattern.length; i++) {
            if (pattern[i] === '?') {
                currentConsecutive++;
                totalEmpty++;
                maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
            } else {
                currentConsecutive = 0;
            }
        }
        
        // Score formula: prioritize longer consecutive runs, then total empty spaces
        // Higher score = better (more consecutive empty spaces)
        // Examples:
        // "CAT???" -> maxConsecutive=3, totalEmpty=3, score=303 (high priority)
        // "C?T??" -> maxConsecutive=2, totalEmpty=3, score=203 (medium priority)
        // "C?T?A" -> maxConsecutive=1, totalEmpty=2, score=102 (lower priority)
        // "C?T?A?" -> maxConsecutive=1, totalEmpty=3, score=103 (lower priority)
        return maxConsecutive * 100 + totalEmpty;
    };

    // Helper function to generate patterns from a starting position
    const generatePatternsFromPosition = (startRow, startCol, direction, getNextPosition) => {
        const patterns = [];
        
        // Generate patterns of different lengths (MAX_WORD_LENGTH down to MIN_WORD_LENGTH)
        for (let length = Math.min(MAX_WORD_LENGTH, size); length >= MIN_WORD_LENGTH; length--) {
            let pattern = "";
            let isValid = true;
            
            // Build pattern for this length
            for (let i = 0; i < length; i++) {
                const { row, col } = getNextPosition(startRow, startCol, i);
                
                // Check bounds
                if (row < 0 || row >= size || col < 0 || col >= size) {
                    isValid = false;
                    break;
                }
                
                const char = square[row][col];
                if (char && char !== "") {
                    pattern += char;
                } else {
                    pattern += "?";
                }
            }
            
            // Only add if pattern is valid and has at least one empty space
            if (isValid && pattern.length === length && pattern.includes('?')) {
                const patternKey = `${startRow}-${startCol}-${direction}-${pattern}`;
                if (!processedPatterns.has(patternKey)) {
                    processedPatterns.add(patternKey);
                                            patterns.push({
                            startRow,
                            startCol,
                            direction,
                            pattern,
                            positions: generatePositions(startRow, startCol, direction, length, "", false),
                            consecutivenessScore: calculateConsecutivenessScore(pattern)
                        });
                }
            }
        }
        
        return patterns;
    };

    // Check all positions for all directions
    for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
            
            // Horizontal patterns (left to right)
            const horizontalPatterns = generatePatternsFromPosition(
                row, col, DIRECTION_HORIZONTAL,
                (startRow, startCol, offset) => ({ row: startRow, col: startCol + offset })
            );
            emptySpaces.push(...horizontalPatterns);
            
            // Vertical patterns (top to bottom)
            const verticalPatterns = generatePatternsFromPosition(
                row, col, DIRECTION_VERTICAL,
                (startRow, startCol, offset) => ({ row: startRow + offset, col: startCol })
            );
            emptySpaces.push(...verticalPatterns);
            
            // Diagonal right-up patterns (top-right to bottom-left)
            const diagonalRightUpPatterns = generatePatternsFromPosition(
                row, col, DIRECTION_DIAGONAL_RIGHT_UP,
                (startRow, startCol, offset) => ({ row: startRow + offset, col: startCol - offset })
            );
            emptySpaces.push(...diagonalRightUpPatterns);
            
            // Diagonal left-up patterns (top-left to bottom-right)
            const diagonalLeftUpPatterns = generatePatternsFromPosition(
                row, col, DIRECTION_DIAGONAL_LEFT_UP,
                (startRow, startCol, offset) => ({ row: startRow + offset, col: startCol + offset })
            );
            emptySpaces.push(...diagonalLeftUpPatterns);
        }
    }

    // Sort by consecutiveness score (highest first), then by length, then by total empty spaces
    return emptySpaces.sort((a, b) => {
        // First priority: consecutiveness score (higher = better)
        if (b.consecutivenessScore !== a.consecutivenessScore) {
            return b.consecutivenessScore - a.consecutivenessScore;
        }
        
        // Second priority: length (longer = better)
        if (b.pattern.length !== a.pattern.length) {
            return b.pattern.length - a.pattern.length;
        }
        
        // Third priority: total empty spaces (fewer = easier to fill)
        const aEmptyCount = (a.pattern.match(/\?/g) || []).length;
        const bEmptyCount = (b.pattern.match(/\?/g) || []).length;
        return aEmptyCount - bEmptyCount;
    });
};

